import React, { useState } from 'react';
import { ReactSVG } from 'react-svg';
import Tooltip from 'react-tooltip-lite';
import Select from 'react-select';
import classNames from 'classnames';

import { i18n as I18n } from 'utils/i18n';

import { selectStyles } from '../../constants/select';
import {
  SHARED_TOOLTIP_DISTANCE,
  DESKTOP_TOOLTIP_DISTANCE,
} from '../../constants/breakpoints';

import useIsMobile from '../../hooks/useIsMobile';

type BookingHost = {
  id: number;
  label: string;
  active: boolean;
};

interface Props {
  bookingHosts: Array<BookingHost>;
  bookingHostId?: number;
  showCreateNewOption?: boolean;
  showToolTip?: boolean;
  hasError?: boolean;
  onSelectNew?: () => void;
  setBookingHostId?: (id: number) => void;
  removeBookingHostId?: () => void;
  disabled: boolean;
  value?: any;
}

const HostSelect: React.FC<Props> = ({
  bookingHosts = [],
  bookingHostId,
  showCreateNewOption = false,
  showToolTip = true,
  hasError = false,
  onSelectNew = () => {},
  setBookingHostId = () => {},
  removeBookingHostId = () => {},
  disabled: isDisabled = false,
  value: valueProp,
  ...props
}) => {
  const NEW_FIELD = {
    value: '__new',
    label: I18n.t('quick_book.add_new_host'),
  };
  const [newFieldSelected, selectNewField] = useState(false);

  const isMobileView = useIsMobile();

  const selectedBookingHost = () => {
    if (valueProp !== undefined) {
      return valueProp && valueProp.value ? valueProp : false;
    }

    if (newFieldSelected) {
      return NEW_FIELD;
    }

    if (bookingHostId) {
      const bookingHost =
        bookingHosts.find(host => host.id === bookingHostId) || {};

      return {
        value: bookingHostId,
        label: bookingHost.label,
      };
    }

    return false;
  };

  const classes = classNames('field', 'field--select-list', {
    'field--has-error': hasError,
  });

  return (
    <div
      className="field__wrapper"
      data-cy="bookingHost"
      data-testid="bookingHostSelect"
    >
      <Select
        label={I18n.t('shared.booking_host')}
        placeholder={I18n.t('shared.select_host')}
        name="booking_host_id"
        styles={selectStyles}
        isSearchable
        isClearable
        isDisabled={isDisabled}
        className={classes}
        options={[
          {
            value: undefined,
            label: I18n.t('shared.select_host'),
          },
          ...bookingHosts
            .filter(host => host.active === true)
            .map(bookingHost => ({
              value: bookingHost.id,
              label: bookingHost.label,
            })),
          ...(showCreateNewOption ? [NEW_FIELD] : []),
        ]}
        onChange={(bookingHost, action) => {
          switch (action.action) {
            case 'select-option': {
              if (bookingHost.value === NEW_FIELD.value) {
                selectNewField(true);
                return onSelectNew();
              }
              selectNewField(false);
              return setBookingHostId(bookingHost.value);
            }

            case 'clear': {
              return removeBookingHostId();
            }

            default: {
              return false;
            }
          }
        }}
        onFocus={() => removeBookingHostId()}
        value={selectedBookingHost()}
        {...props}
      />

      {showToolTip && (
        <Tooltip
          content={I18n.t('quick_book.help_text')}
          className="quick-book__tooltip"
          distance={
            isMobileView ? SHARED_TOOLTIP_DISTANCE : DESKTOP_TOOLTIP_DISTANCE
          }
        >
          <ReactSVG
            src="/images/icons/blue-question-mark.svg"
            width={17}
            height={17}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default HostSelect;
